import * as React from 'react'
import type { FieldDto, FieldValueDto, SignatureValueDto } from '@lib/dto/envelope.dto'
import { bindFieldValue } from '@lib/helpers/field-value'
import { fieldNeedsInk, isFieldFilled } from './field-fill'
import { Tooltip } from '@/components/ui/tooltip'

export function FieldOverlay({
  field,
  value,
  page,
  viewerSignerId,
  signerSigned,
  onChange,
  onClickSign,
}: {
  field: FieldDto
  value: FieldValueDto | undefined
  page: { widthPts: number; heightPts: number }
  viewerSignerId: string
  signerSigned: boolean
  onChange: (value: FieldValueDto) => void
  onClickSign: () => void
}) {
  const style: React.CSSProperties = {
    left: `${(field.x / page.widthPts) * 100}%`,
    top: `${(field.y / page.heightPts) * 100}%`,
    width: `${(field.width / page.widthPts) * 100}%`,
    height: `${(field.height / page.heightPts) * 100}%`,
  }

  return (
    <div className="absolute" style={style}>
      <FieldControl
        field={field}
        value={value}
        viewerSignerId={viewerSignerId}
        signerSigned={signerSigned}
        onChange={onChange}
        onClickSign={onClickSign}
      />
    </div>
  )
}

export function FieldControl({
  field,
  value,
  viewerSignerId,
  signerSigned,
  onChange,
  onClickSign,
}: {
  field: FieldDto
  value: FieldValueDto | undefined
  viewerSignerId?: string
  /** Whether the field's signer has signed, so an empty field of theirs was left empty rather than not reached. */
  signerSigned?: boolean
  onChange: (value: FieldValueDto) => void
  onClickSign: () => void
}) {
  const otherParty =
    viewerSignerId != null && field.signerId !== viewerSignerId
  // Another signer's value comes from the session, which carries it only once
  // they've signed; this page never edits it.
  const shown = otherParty ? (field.value ?? undefined) : value
  const bound = bindFieldValue(field, shown)
  const filled = isFieldFilled(field, shown)
  if (otherParty && !filled && signerSigned !== true) {
    return <WaitingForSigner field={field} />
  }
  const actionable = fieldNeedsInk(field) && !otherParty
  const boxClass = fieldBoxClass(field, filled, actionable)

  switch (bound.type) {
    case 'signature':
    case 'initials':
      return (
        <button
          id={`sign-field-${field.id}`}
          type="button"
          disabled={field.readOnly || otherParty}
          className={`overflow-hidden rounded-sm border h-full w-full ${boxClass}`}
          onClick={() => {
            onClickSign()
          }}
        >
          <SignaturePreview value={bound.value} />
        </button>
      )
    case 'date_signed':
      return (
        <div
          id={`sign-field-${field.id}`}
          className={`pointer-events-none flex items-center overflow-hidden rounded-sm border px-1 text-[11px] text-foreground h-full w-full ${boxClass}`}
        >
          {bound.value ?? ''}
        </div>
      )
    case 'checkbox':
      return (
        <button
          id={`sign-field-${field.id}`}
          type="button"
          disabled={field.readOnly || otherParty}
          className={`flex items-center justify-center rounded-sm border h-full w-full ${boxClass}`}
          onClick={() => onChange(bound.value !== true)}
        >
          {bound.value === true ? <span className="text-sm font-semibold">✓</span> : null}
        </button>
      )
    case 'radio':
      return (
        <button
          id={`sign-field-${field.id}`}
          type="button"
          disabled={field.readOnly || otherParty}
          className={`flex items-center justify-center rounded-full border h-full w-full ${boxClass}`}
          onClick={() => onChange(bound.field.name)}
        >
          {bound.value !== undefined && bound.value.length > 0 ? (
            <span className="h-2 w-2 rounded-full bg-foreground" />
          ) : null}
        </button>
      )
    case 'dropdown':
      return (
        <select
          id={`sign-field-${field.id}`}
          className={`rounded-sm border text-[11px] h-full w-full ${boxClass}`}
          value={bound.value ?? ''}
          disabled={field.readOnly || otherParty}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Select</option>
          {(bound.field.options ?? []).map((option) => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </select>
      )
    case 'text':
    case 'date':
      return (
        <input
          id={`sign-field-${field.id}`}
          className={`rounded-sm border px-1 text-[11px] text-foreground outline-none h-full w-full ${boxClass}`}
          type={bound.type === 'date' ? 'date' : 'text'}
          value={bound.value ?? ''}
          disabled={field.readOnly || otherParty}
          maxLength={bound.field.maxLength ?? undefined}
          placeholder={actionable && !field.required ? 'Optional' : undefined}
          onChange={(event) => onChange(event.target.value)}
        />
      )
  }
}

/**
 * How an empty box the signer can fill looks: required ones in the attention
 * orange, optional ones quieter, so the orange always means "Finish waits on
 * this". Filled boxes and ones the signer can't fill stay plain.
 */
function fieldBoxClass(field: FieldDto, filled: boolean, actionable: boolean): string {
  if (filled) {
    return 'border-transparent bg-background/80'
  }
  if (!actionable) {
    return 'border-transparent bg-transparent'
  }
  return field.required ? 'sign-field-empty' : 'sign-field-optional'
}

function WaitingForSigner({ field }: { field: FieldDto }) {
  const label =
    field.type === 'date_signed' ? 'Pending' : `Waiting for ${field.signerName}`
  return (
    <Tooltip content={`Waiting for ${field.signerName}`}>
      <div
        id={`sign-field-${field.id}`}
        className="sign-field-waiting overflow-hidden rounded-sm border h-full w-full"
      >
        <span className="block truncate px-1 text-[10px] leading-tight">{label}</span>
      </div>
    </Tooltip>
  )
}

function SignaturePreview({ value }: { value: SignatureValueDto | undefined }) {
  if (!value || value.data.trim().length === 0) {
    return null
  }
  if (value.kind === 'draw') {
    return (
      <img src={value.data} alt="" className="h-full w-full object-contain p-0.5" />
    )
  }
  return (
    <span className="block truncate px-1 font-serif italic leading-none text-foreground">
      {value.data}
    </span>
  )
}

